import React, { useEffect, useState } from 'react';
import {
    Container,
    Box,
    Alert,
    Typography,
    Paper,
    Fab,
    Chip,
    useTheme,
    useMediaQuery
} from '@mui/material';
import { TrendingUp, Edit, Lock } from '@mui/icons-material';
import moment, { Moment } from 'moment';
import { Responsive, WidthProvider, Layout } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import analyticsService, { AnalyticsSummaryWithTrends } from '../../../../services/analytics/AnalyticsService';
import AnalyticsSummaryCards from './components/AnalyticsSummaryCards';
import ActivityChart from './components/ActivityChart';
import AuthChart from './components/AuthChart';
import ConversionChart from './components/ConversionChart';
import { PortalScreenHeader } from '../../../../components/PortalScreenHeader';
import { tablePreferencesStore } from '../../../../app/stores/TablePreferences';
import { useStoreConfig } from '../../../../contexts/StoreConfigContext';

const ResponsiveGridLayout = WidthProvider(Responsive);

export type DateRange = [Moment, Moment];

// Default layout configuration for all breakpoints
const defaultLayouts = {
    lg: [
        { i: 'activity', x: 0, y: 0, w: 12, h: 5, minW: 2, minH: 4 },
        { i: 'auth', x: 0, y: 5, w: 6, h: 4, minW: 2, minH: 3 },
        { i: 'conversion', x: 6, y: 5, w: 6, h: 4, minW: 2, minH: 3 },
    ],
    md: [
        { i: 'activity', x: 0, y: 0, w: 10, h: 5, minW: 2, minH: 4 },
        { i: 'auth', x: 0, y: 5, w: 5, h: 4, minW: 2, minH: 3 },
        { i: 'conversion', x: 5, y: 5, w: 5, h: 4, minW: 2, minH: 3 },
    ],
    sm: [
        { i: 'activity', x: 0, y: 0, w: 6, h: 6, minW: 2, minH: 5 },
        { i: 'auth', x: 0, y: 6, w: 6, h: 5, minW: 2, minH: 4 },
        { i: 'conversion', x: 0, y: 11, w: 6, h: 5, minW: 2, minH: 4 },
    ],
    xs: [
        { i: 'activity', x: 0, y: 0, w: 4, h: 6, minW: 2, minH: 5 },
        { i: 'auth', x: 0, y: 6, w: 4, h: 5, minW: 2, minH: 4 },
        { i: 'conversion', x: 0, y: 11, w: 4, h: 5, minW: 2, minH: 4 },
    ],
    xxs: [
        { i: 'activity', x: 0, y: 0, w: 2, h: 6, minW: 2, minH: 5 },
        { i: 'auth', x: 0, y: 6, w: 2, h: 5, minW: 2, minH: 4 },
        { i: 'conversion', x: 0, y: 11, w: 2, h: 5, minW: 2, minH: 4 },
    ],
};

const CustomerAnalytics: React.FC = () => {
    // Get preferences from store
    const { 
        analytics: analyticsPrefs, 
        updateAnalyticsPreferences 
    } = tablePreferencesStore();
    
    const [summary, setSummary] = useState<AnalyticsSummaryWithTrends | null>(null);
    const [loadingSummary, setLoadingSummary] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [dateRange, setDateRange] = useState<DateRange>([
        moment().subtract(7, 'days').startOf('day'),
        moment().endOf('day')
    ]);
    const [isEditMode, setIsEditMode] = useState(false);
    const [layouts, setLayouts] = useState<{
        lg: Layout[];
        md: Layout[];
        sm: Layout[];
        xs: Layout[];
        xxs: Layout[]
    }>(defaultLayouts);
    const [activeTimeFilter, setActiveTimeFilter] = useState<string>(analyticsPrefs.activeTimeFilter);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Load saved layout from localStorage
    useEffect(() => {
        const savedLayout = localStorage.getItem('analyticsLayout');
        if (savedLayout) {
            try {
                const parsed = JSON.parse(savedLayout);
                // Ensure all breakpoints exist, merging with defaults
                setLayouts({
                    lg: parsed.lg || defaultLayouts.lg,
                    md: parsed.md || defaultLayouts.md,
                    sm: parsed.sm || defaultLayouts.sm,
                    xs: parsed.xs || defaultLayouts.xs,
                    xxs: parsed.xxs || defaultLayouts.xxs,
                });
            } catch (e) {
                console.error('Failed to load saved layout:', e);
            }
        }
    }, []);

    const fetchAnalyticsSummary = async () => {
        setLoadingSummary(true);
        setError(null);

        try {
            // Calculate current period
            const currentStart = dateRange[0].toISOString();
            const currentEnd = dateRange[1].toISOString();

            // Calculate previous period (same duration as current period)
            const durationInDays = dateRange[1].diff(dateRange[0], 'days');
            const previousStart = dateRange[0].clone().subtract(durationInDays + 1, 'days').toISOString();
            const previousEnd = dateRange[0].clone().subtract(1, 'day').endOf('day').toISOString();

            // Fetch both current and previous period data
            const [currentResult, previousResult] = await Promise.all([
                analyticsService.getAnalyticsSummary(currentStart, currentEnd),
                analyticsService.getAnalyticsSummary(previousStart, previousEnd)
            ]);

            if (currentResult.success && currentResult.data) {
                const currentData = currentResult.data;
                const previousData = previousResult.data;

                // Calculate trends (percentage change)
                const calculateTrend = (current: number, previous: number): number => {
                    if (previous === 0) return current > 0 ? 100 : 0;
                    return ((current - previous) / previous) * 100;
                };

                const summaryWithTrends: AnalyticsSummaryWithTrends = {
                    ...currentData,
                    trends: previousData ? {
                        totalVisits: calculateTrend(currentData.totalVisits, previousData.totalVisits),
                        uniqueVisitors: calculateTrend(currentData.uniqueVisitors, previousData.uniqueVisitors),
                        totalPageViews: calculateTrend(currentData.totalPageViews, previousData.totalPageViews),
                        totalProductViews: calculateTrend(currentData.totalProductViews, previousData.totalProductViews),
                        anonymousUsers: calculateTrend(currentData.anonymousUsers, previousData.anonymousUsers),
                        authenticatedUsers: calculateTrend(currentData.authenticatedUsers, previousData.authenticatedUsers)
                    } : undefined
                };

                setSummary(summaryWithTrends);
            } else {
                console.error('❌ Failed to fetch summary:', currentResult.message);
                setError(currentResult.message || 'Failed to fetch analytics summary');
            }
        } catch (error: any) {
            console.error('❌ Error fetching summary:', error);
            setError(error.message || 'Failed to fetch analytics summary');
        } finally {
            setLoadingSummary(false);
        }
    };

    useEffect(() => {
        fetchAnalyticsSummary();
    }, [dateRange]);

    const handleQuickRange = (value: number, unit: 'hours' | 'days' | 'special') => {
        if (unit === 'special') {
            if (value === 0) {
                // Today
                setDateRange([moment().startOf('day'), moment().endOf('day')]);
            } else if (value === 1) {
                // This Month
                setDateRange([moment().startOf('month'), moment().endOf('month')]);
            } else if (value === 2) {
                // This Year
                setDateRange([moment().startOf('year'), moment().endOf('year')]);
            }
        } else if (unit === 'hours') {
            setDateRange([moment().subtract(value, 'hours'), moment()]);
        } else {
            setDateRange([moment().subtract(value, 'days').startOf('day'), moment().endOf('day')]);
        }
    };

    const handleLayoutChange = (_currentLayout: Layout[], allLayouts: { [key: string]: Layout[] }) => {
        if (isEditMode) {
            const newLayouts = {
                lg: allLayouts.lg || layouts.lg,
                md: allLayouts.md || layouts.md,
                sm: allLayouts.sm || layouts.sm,
                xs: allLayouts.xs || layouts.xs,
                xxs: allLayouts.xxs || layouts.xxs,
            };
            setLayouts(newLayouts);
            // Save to localStorage
            localStorage.setItem('analyticsLayout', JSON.stringify(newLayouts));
        }
    };

    const toggleEditMode = () => {
        setIsEditMode(!isEditMode);
    };

    const { config } = useStoreConfig();

    const timeFilters = [
        { label: '6 Hours', value: '6h' },
        { label: '12 Hours', value: '12h' },
        { label: '24 Hours', value: '24h' },
        { label: '48 Hours', value: '48h' },
        { label: '72 Hours', value: '72h' },
        { label: 'Today', value: 'today' },
        { label: 'Last 7 Days', value: '7d' },
        { label: 'Last 14 Days', value: '14d' },
        { label: 'Last 30 Days', value: '30d' },
        { label: 'This Month', value: 'month' },
        { label: 'This Year', value: 'year' }
    ];

    const handleFilterChange = (value: string) => {
        setActiveTimeFilter(value);
        updateAnalyticsPreferences({ activeTimeFilter: value });

        switch (value) {
            case '6h': handleQuickRange(6, 'hours'); break;
            case '12h': handleQuickRange(12, 'hours'); break;
            case '24h': handleQuickRange(24, 'hours'); break;
            case '48h': handleQuickRange(48, 'hours'); break;
            case '72h': handleQuickRange(72, 'hours'); break;
            case 'today': handleQuickRange(0, 'special'); break;
            case '7d': handleQuickRange(7, 'days'); break;
            case '14d': handleQuickRange(14, 'days'); break;
            case '30d': handleQuickRange(30, 'days'); break;
            case 'month': handleQuickRange(1, 'special'); break;
            case 'year': handleQuickRange(2, 'special'); break;
        }
    };

    return (
        <Container component='main' maxWidth='xl' sx={{ paddingX: { xs: 0, md: 2 }, pb: 8 }}>
            <PortalScreenHeader
                title='Customer Analytics'
                subtitle='Track visitor behavior and engagement metrics'
                icon={<TrendingUp />}
                onRefresh={fetchAnalyticsSummary}
                refreshing={loadingSummary}
                actions={
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Fab
                            size='small'
                            color={isEditMode ? 'warning' : 'primary'}
                            onClick={toggleEditMode}
                            sx={{
                                boxShadow: theme.palette.mode === 'dark'
                                    ? '0 2px 8px rgba(0, 0, 0, 0.4)'
                                    : '0 2px 8px rgba(0, 0, 0, 0.15)',
                                width: 40,
                                height: 40,
                                minHeight: 40,
                            }}
                        >
                            {isEditMode ? <Lock /> : <Edit />}
                        </Fab>
                    </Box>
                }
            />

            {error && (
                <Alert severity='error' sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Time Range Filter Chips - Always visible and scrollable */}
            <Box
                sx={{
                    display: 'flex',
                    gap: 1,
                    overflowX: 'auto',
                    pb: 1,
                    mb: 2,
                    px: 1,
                    scrollbarWidth: 'none',
                    '&::-webkit-scrollbar': { display: 'none' },
                    WebkitOverflowScrolling: 'touch',
                }}
            >
                {timeFilters.map((filter) => {
                    const isActive = activeTimeFilter === filter.value;
                    return (
                        <Chip
                            key={filter.value}
                            label={filter.label}
                            variant={isActive ? 'filled' : 'outlined'}
                            onClick={() => handleFilterChange(filter.value)}
                            size='small'
                            sx={{
                                flexShrink: 0,
                                fontWeight: isActive ? 600 : 400,
                                px: 1,
                                py: 2.5,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease-in-out',
                                ...(isActive && {
                                    backgroundColor: config.primaryColor,
                                    color: '#fff',
                                    borderColor: config.primaryColor,
                                    boxShadow: `0 0 0 2px ${config.primaryColor}40`,
                                }),
                                '&:hover': {
                                    transform: 'translateY(-1px)',
                                    boxShadow: theme.palette.mode === 'dark'
                                        ? '0 2px 8px rgba(0, 0, 0, 0.4)'
                                        : '0 2px 8px rgba(0, 0, 0, 0.15)',
                                    ...(isActive && {
                                        backgroundColor: config.primaryColor,
                                    }),
                                },
                            }}
                        />
                    );
                })}
            </Box>

            {/* Date Range and Stats Summary */}
            {summary && (
                <Paper 
                    elevation={0} 
                    sx={{ 
                        mb: 3, 
                        mt: 2,
                        p: isMobile ? 1.5 : 2,
                        backgroundColor: theme.palette.mode === 'dark' 
                            ? 'rgba(255, 255, 255, 0.05)' 
                            : 'rgba(0, 0, 0, 0.02)',
                        border: `1px solid ${theme.palette.divider}`
                    }}
                >
                    <Box sx={{
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        alignItems: isMobile ? 'flex-start' : 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: isMobile ? 1 : 2
                    }}>
                        <Typography variant='body2' sx={{ fontFamily: 'primaryFont', color: 'text.secondary', fontSize: isMobile ? '0.75rem' : undefined }}>
                            <strong>Date Range:</strong> {dateRange[0].format(isMobile ? 'MMM D' : 'MMM D, YYYY')} - {dateRange[1].format(isMobile ? 'MMM D' : 'MMM D, YYYY')}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: isMobile ? 2 : 3, flexWrap: 'wrap' }}>
                            <Typography variant='body2' sx={{ fontFamily: 'primaryFont', fontSize: isMobile ? '0.75rem' : undefined }}>
                                <strong>{summary.uniqueVisitors.toLocaleString()}</strong> <span style={{ color: 'gray' }}>{isMobile ? 'visitors' : 'unique visitors'}</span>
                            </Typography>
                            <Typography variant='body2' sx={{ fontFamily: 'primaryFont', fontSize: isMobile ? '0.75rem' : undefined }}>
                                <strong>{summary.totalPageViews.toLocaleString()}</strong> <span style={{ color: 'gray' }}>{isMobile ? 'views' : 'page views'}</span>
                            </Typography>
                        </Box>
                    </Box>
                </Paper>
            )}

            <Box sx={{ mb: 3 }}>
                <AnalyticsSummaryCards summary={summary} loading={loadingSummary} />
            </Box>

            {/* Customizable Grid Layout */}
            <ResponsiveGridLayout
                className='layout'
                layouts={layouts}
                breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
                cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
                rowHeight={isMobile ? 70 : 40}
                onLayoutChange={handleLayoutChange}
                isDraggable={isEditMode && !isMobile}
                isResizable={isEditMode && !isMobile}
                compactType='vertical'
                preventCollision={false}
                margin={isMobile ? [12, 12] : [20, 20]}
                containerPadding={[0, 0]}
                draggableHandle='.drag-handle'
                resizeHandles={['se']}
            >
                <Box key='activity' sx={{
                    height: isMobile ? '100%' : 'auto',
                    border: isEditMode ? '2px dashed rgba(128, 0, 128, 0.3)' : 'none',
                    borderRadius: 2,
                }}>
                    <Box
                        className={isEditMode ? 'drag-handle' : ''}
                        sx={{
                            height: isMobile ? '100%' : 'auto',
                            cursor: isEditMode ? 'move' : 'default',
                            overflow: 'visible',
                        }}
                    >
                        <ActivityChart dateRange={dateRange} />
                    </Box>
                </Box>

                <Box key='auth' sx={{
                    height: isMobile ? '100%' : 'auto',
                    border: isEditMode ? '2px dashed rgba(128, 0, 128, 0.3)' : 'none',
                    borderRadius: 2,
                }}>
                    <Box
                        className={isEditMode ? 'drag-handle' : ''}
                        sx={{
                            height: isMobile ? '100%' : 'auto',
                            cursor: isEditMode ? 'move' : 'default',
                            overflow: 'visible',
                        }}
                    >
                        <AuthChart dateRange={dateRange} />
                    </Box>
                </Box>

                <Box key='conversion' sx={{
                    height: isMobile ? '100%' : 'auto',
                    border: isEditMode ? '2px dashed rgba(128, 0, 128, 0.3)' : 'none',
                    borderRadius: 2,
                }}>
                    <Box
                        className={isEditMode ? 'drag-handle' : ''}
                        sx={{
                            height: isMobile ? '100%' : 'auto',
                            cursor: isEditMode ? 'move' : 'default',
                            overflow: 'visible',
                        }}
                    >
                        <ConversionChart dateRange={dateRange} />
                    </Box>
                </Box>
            </ResponsiveGridLayout>
        </Container>
    );
};

export default CustomerAnalytics;